import { rem, type MantineColorsTuple } from '@mantine/core';

export const PALETTE = {
  /** Primary brand — teal/cyan */
  horizon: [
    '#e6fafa',
    '#c0f0f0',
    '#87e0e0',
    '#44cccc',
    '#14b8b8',
    '#0d9b9b',
    '#097a7a',
    '#065858',
    '#033838',
    '#011c1c',
  ] as MantineColorsTuple,

  /** Accent — warm amber */
  sand: [
    '#fff8ed',
    '#fdecd0',
    '#fad4a0',
    '#f6b760',
    '#f39c2a',
    '#e0851a',
    '#c06a0e',
    '#9a5009',
    '#713806',
    '#472103',
  ] as MantineColorsTuple,

  /** Slate — cool dark gray; drives dark scheme surfaces */
  slate: [
    '#f4f5f7',
    '#e0e3ea',
    '#bcc2cf',
    '#8d97a8',
    '#606b7c',
    '#414b5a',
    '#2a3241',
    '#1e2633',
    '#141b27',
    '#0e1117',
  ] as MantineColorsTuple,
} as const;

export const FONTS = {
  sans: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  serif: 'Fraunces, Georgia, serif',
  mono: 'JetBrains Mono, Menlo, monospace',
} as const;

export const FONT_SIZES = {
  xs: rem(11),
  sm: rem(13),
  md: rem(15),
  lg: rem(17),
  xl: rem(20),
} as const;

export const HEADING_SIZES = {
  h1: { fontSize: rem(34), lineHeight: '1.15' },
  h2: { fontSize: rem(26), lineHeight: '1.2' },
  h3: { fontSize: rem(20), lineHeight: '1.3' },
  h4: { fontSize: rem(17), lineHeight: '1.4' },
  h5: { fontSize: rem(15), lineHeight: '1.5' },
  h6: { fontSize: rem(13), lineHeight: '1.5' },
} as const;

export const RADIUS = {
  xs: rem(4),
  sm: rem(8),
  md: rem(12),
  lg: rem(16),
  xl: rem(24),
} as const;

/** Dark-scheme tuned; app uses forceColorScheme="dark". */
export const SHADOWS = {
  xs: '0 1px 3px rgba(0,0,0,0.35)',
  sm: '0 2px 8px rgba(0,0,0,0.40)',
  md: '0 4px 16px rgba(0,0,0,0.45)',
  lg: '0 8px 32px rgba(0,0,0,0.50)',
  xl: '0 16px 48px rgba(0,0,0,0.55)',
} as const;

/** Occasion cover-band and badge colors (fixed decorative hex on dark cards). */
export const OCCASION_COLORS: Record<
  string,
  {
    coverBg: string;
    badgeFill: string;
    badgeText: string;
  }
> = {
  HONEYMOON: { coverBg: '#FBEAF0', badgeFill: '#F4C0D1', badgeText: '#72243E' },
  ANNIVERSARY: {
    coverBg: '#FBEAF0',
    badgeFill: '#F4C0D1',
    badgeText: '#72243E',
  },
  BABYMOON: { coverBg: '#FBEAF0', badgeFill: '#F4C0D1', badgeText: '#72243E' },
  BACHELORETTE: {
    coverBg: '#FBEAF0',
    badgeFill: '#F4C0D1',
    badgeText: '#72243E',
  },
  BACHELOR: { coverBg: '#FBEAF0', badgeFill: '#F4C0D1', badgeText: '#72243E' },
  FAMILY: { coverBg: '#E1F5EE', badgeFill: '#9FE1CB', badgeText: '#085041' },
  CAMPING: { coverBg: '#EAF3DE', badgeFill: '#C0DD97', badgeText: '#27500A' },
  ROAD_TRIP: { coverBg: '#EAF3DE', badgeFill: '#C0DD97', badgeText: '#27500A' },
  GRADUATION: {
    coverBg: '#FAEEDA',
    badgeFill: '#FAC775',
    badgeText: '#633806',
  },
  BIRTHDAY: { coverBg: '#FAEEDA', badgeFill: '#FAC775', badgeText: '#633806' },
  RETIREMENT: {
    coverBg: '#FAEEDA',
    badgeFill: '#FAC775',
    badgeText: '#633806',
  },
  BUSINESS: { coverBg: '#F1EFE8', badgeFill: '#D3D1C7', badgeText: '#444441' },
  OTHER: { coverBg: '#F1EFE8', badgeFill: '#D3D1C7', badgeText: '#444441' },
} as const;

export const OCCASION_DEFAULT_COLORS = OCCASION_COLORS.OTHER;

/** Status badge colors. */
export const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  PLANNING: { bg: '#E6F1FB', text: '#0C447C' },
  ACTIVE: { bg: '#EAF3DE', text: '#27500A' },
  COMPLETED: { bg: '#F1EFE8', text: '#444441' },
  CANCELLED: { bg: '#FCEBEB', text: '#791F1F' },
} as const;
